import { Box, Button, Form, Grid, Text } from '@factorialco/deprecated-design-system'
import {
  AtsGetJobPostingSettingsBasicInfoDocument,
  AtsJobPostingSettingsBasicInformationFragment,
  AtsJobPostingSettingsLocationsFragment,
  AtsJobPostingSettingsTeamsFragment,
  AtsUpdateJobPostingBasicInfoDocument,
  AtsWorkplaceTypesEnum,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { countryName } from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { BasicInformationSkeleton } from './BasicInformationSkeleton'

interface Props {
  jobId: number
}

export const BasicInformation = ({ jobId }: Props) => {
  const { data, loading } = useQuery(AtsGetJobPostingSettingsBasicInfoDocument, {
    variables: {
      id: jobId,
    },
  })

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccessBasicInformation)
  }, [])

  const job = data?.ats.jobPosting
  const locations = data?.locations.locations ?? []
  const teams = data?.teams.teams ?? []

  if (loading || !job) {
    return <BasicInformationSkeleton />
  }

  return <BasicInformationForm job={job} locations={locations} teams={teams} />
}

interface UpdateJobPostingBasicInfoForm {
  title: string
  legalEntityId: number | null
  locationId: number | null
  teamId: number | null
  workplaceType: AtsWorkplaceTypesEnum | null
  city: string
  state: string
  country: string
  postalCode: string
}

interface BasicInformationFormProps {
  job: AtsJobPostingSettingsBasicInformationFragment
  locations: AtsJobPostingSettingsLocationsFragment[]
  teams: AtsJobPostingSettingsTeamsFragment[]
}

const BasicInformationForm = ({ job, locations, teams }: BasicInformationFormProps) => {
  const { company } = useCurrentSession()

  const [updateJobPosting] = useMutation(AtsUpdateJobPostingBasicInfoDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsJobPosting,
  })

  const form = useForm<UpdateJobPostingBasicInfoForm>({
    defaultValues: {
      title: job.title,
      legalEntityId: job.legalEntity?.id ?? null,
      teamId: job.team?.id || null,
      workplaceType: job.workplaceType ?? null,
      locationId: job.location?.id ?? null,
      city: job.location?.city ?? '',
      state: job.location?.state ?? '',
      country: job.location?.country ?? '',
      postalCode: job.location?.postalCode ?? '',
    },
    validate(values) {
      const errors: FormErrors<UpdateJobPostingBasicInfoForm> = {}

      if (values.title === '') {
        errors.title = i18n.t('common.validations.empty')
      }

      if (!values.locationId) {
        errors.locationId = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await updateJobPosting({
        variables: {
          id: job.id,
          title: values.title,
          teamId: values.teamId,
          legalEntityId: values.legalEntityId,
          workplaceType: values.workplaceType,
          locationId: values.locationId,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  const onChangeLocation = (value: number) => {
    const locationData = locations.find((location) => location.id === value)
    if (!locationData) return

    form.setValues({
      ...form.values,
      locationId: value,
      city: locationData.city ?? '',
      state: locationData.state ?? '',
      country: locationData.country ?? '',
      postalCode: locationData.postalCode ?? '',
    })
  }

  return (
    <Form onSubmit={() => form.handleSubmit()} padding={null}>
      <Text size="400" color="grey1000" weight="semibold">
        {i18n.t('ats.job_posting_settings.basic_information.basic_info_title')}
      </Text>

      <Box gap="s16" paddingBottom="s24">
        <TextField
          label={i18n.t('ats.job_posting_settings.basic_information.job_opening_label')}
          value={form.values.title}
          onChange={(title) => form.setFieldValue('title', title)}
          description={i18n.t('ats.job_posting_settings.basic_information.job_opening_description')}
          error={form.errors.title}
          mandatory
        />
        {(company.legalEntities.length > 1 || !form.values.legalEntityId) && (
          <Select
            none={i18n.t('common.select_basic')}
            label={i18n.t('ats.job_posting_settings.basic_information.legal_entity_label')}
            value={form.values.legalEntityId?.toString()}
            onChange={(value) => form.setFieldValue('legalEntityId', Number(value))}
            options={company.legalEntities.map((entity) => ({
              label: `${entity.legalName} - ${entity.currency}`,
              value: entity.id,
            }))}
          />
        )}
        <Select
          none={i18n.t('common.select_basic')}
          label={i18n.t('ats.job_posting_settings.basic_information.team_label')}
          value={form.values.teamId?.toString()}
          onChange={(value) => form.setFieldValue('teamId', Number(value))}
          options={teams.map((team) => ({
            label: team.name,
            value: team.id,
          }))}
        />

        <Select
          none={i18n.t('common.select_basic')}
          label={i18n.t('ats.job_posting_settings.basic_information.type_of_location_label')}
          value={form.values.workplaceType ?? ''}
          onChange={(value) => form.setFieldValue('workplaceType', value)}
          options={[
            {
              label: i18n.t('ats.workplace_types.onsite'),
              value: 'onsite',
            },
            {
              label: i18n.t('ats.workplace_types.hybrid'),
              value: 'hybrid',
            },
            {
              label: i18n.t('ats.workplace_types.remote'),
              value: 'remote',
            },
          ]}
        />
      </Box>
      <Text size="400" color="grey1000" weight="semibold">
        {i18n.t('ats.job_posting_settings.basic_information.location_title')}
      </Text>
      <Box gap="s16" paddingBottom="s24">
        <Select
          none={i18n.t('common.select_basic')}
          label={i18n.t('ats.job_posting_settings.basic_information.workplace_label')}
          value={form.values.locationId?.toString()}
          onChange={(value) => onChangeLocation(Number(value))}
          options={locations.map((location) => {
            return {
              label: location.name,
              value: location.id,
            }
          })}
          error={form.errors.locationId}
          mandatory
        />
        <Grid variant="fixed">
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <TextField
              label={i18n.t('ats.job_posting_settings.basic_information.country_label')}
              value={countryName(form.values.country)}
              readonly
            />
          </Box>
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <TextField
              label={i18n.t('ats.job_posting_settings.basic_information.state_label')}
              value={form.values.state}
              readonly
            />
          </Box>
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <TextField
              label={i18n.t('ats.job_posting_settings.basic_information.city_label')}
              value={form.values.city}
              readonly
            />
          </Box>
          <Box gridColSpan={{ xxs: 1, xs: 6 }}>
            <TextField
              label={i18n.t('ats.job_posting_settings.basic_information.zip_code_label')}
              value={form.values.postalCode}
              readonly
            />
          </Box>
        </Grid>
      </Box>

      <Callout type="warning" description={i18n.t('ats.jobs_form.workplace_warning_message')} />

      {!form.isValid && (
        <Callout type="warning" description={i18n.t('ats.jobs_form.missing_fields_warning')} />
      )}

      <Button disabled={form.isSubmitting || !form.isValid} submit>
        {i18n.t('common.submit')}
      </Button>
    </Form>
  )
}
